import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import type { EntryPointProps } from 'react-relay';
import type { AIEventBaseAttributes } from '@atlassian/ai-analytics';
import {
	ContextualAnalyticsData,
	useAnalyticsEvents,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import { AiFooter } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-footer/index.tsx';
import { ProactiveContainer } from '@atlassian/jira-ai-proactive-container/src/index.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useRelatedIssuesActions } from '@atlassian/jira-ai-related-issues/src/controllers/context.tsx';
import { RELATED_ISSUES_AI_EXPERIENCE } from '@atlassian/jira-atlassian-intelligence/src/common/ui/feedback-form/index.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import {
	type RelatedIssuesSearchPayload,
	useFetchRelatedIssue,
} from './services/use-fetch-related-issues/index.tsx';
import {
	JiraIssueRelatedIssuesContainer,
	useRelatedIssues,
	type InvokedFrom,
} from './controllers/context.tsx';
import { SuggestRelatedIssuesModal } from './ui/index.tsx';
import type { onIssueLinkSave } from './ui/types.tsx';
import { ProactiveSimilarIssuesCollapsible } from './ui/proactive-collapsible-parent/index.tsx';
import { RelatedIssuesDraftList } from './ui/related-issues-draft-list/index.tsx';
import { Footer } from './ui/footer/index.tsx';
import messages from './messages.tsx';
import { useRelatesToIssueLinkType } from './utils/index.tsx';

type RelatedIssuesAttributes = AIEventBaseAttributes & { singleInstrumentationId: string };

const getAiEventAttributes = (invokedFrom?: InvokedFrom): AIEventBaseAttributes => ({
	aiFeatureName: 'jiraAIRelatedIssues',
	isAIFeature: 1,
	proactiveGeneratedAI:
		invokedFrom === 'issueLinksAddButton' ||
		invokedFrom === 'linkedWorkItemQuickAdd' ||
		invokedFrom === 'globalIssueCreate'
			? 1
			: 0,
	userGeneratedAI: invokedFrom === 'improveIssueDropdown' ? 1 : 0,
});

const AI_EVENT_ATTRIBUTES: AIEventBaseAttributes = {
	aiFeatureName: 'jiraAIRelatedIssues',
	isAIFeature: 1,
	proactiveGeneratedAI: 0,
	userGeneratedAI: 1,
};

const FEEDBACK_ENTRYPOINT_ID = 'f91a821a-c7b4-47a6-bc0c-b99c234f3124';
const FEEDBACK_COLLECTOR_ID = '043ab007-eb05-4694-9742-302b0a7f41a6';

// Base props that are common to both experiment proactive and non-proactive modes
type BaseProps = {
	issueKey: string;
	onIssueLinkSave: onIssueLinkSave; // Required in non-proactive mode
	existingIssues: string[];
	customRelatedIssueSearchPayload?: RelatedIssuesSearchPayload;
	invokedFromSource?: InvokedFrom; // Optional in proactive mode
	onCopyButtonClick?: (issueKey: string) => void; // Optional in proactive mode
	headerTitle?: MessageDescriptor;
};

// Props when experiment is enabled (proactive mode)
type ProactiveProps = BaseProps & {
	panelDisplay?: never;
	onRelatedIssuesTriggered?: never;
};

// Props when experiment is disabled (non-proactive mode)
type NonProactiveProps = BaseProps & {
	existingIssues: string[]; // Required in non-proactive mode
	panelDisplay?: boolean; // Required in non-proactive mode
	onRelatedIssuesTriggered: (arg: boolean) => void; // Required in non-proactive mode
};

type Props = ProactiveProps | NonProactiveProps;

const UNCATEGORISED_ERROR = 'UNKNOWN ERROR';

/**
 * When we are in the non-proactive mode, we use this component to control the display of the modal.
 * When we are in the proactive mode, we use this as a wrapper for the proactive modal, and control display inside the collapsible parent.
 * On proactive-jira-ai-similar-issues experiment cleanup, rename this to SuggestRelatedWorkItemsContentContainer.
 */
export const SuggestRelatedIssuesModalContainer = (props: Props) => {
	const {
		issueKey,
		existingIssues,
		onIssueLinkSave,
		panelDisplay,
		onRelatedIssuesTriggered,
		invokedFromSource,
		onCopyButtonClick,
		headerTitle = messages.linkSimilarWorkItems,
	} = props;

	const [shouldDisplayModal, setShouldDisplayModal] = useState(
		!expVal('proactive-jira-ai-similar-issues', 'isEnabled', false) ? !props.panelDisplay : true,
	);

	const { setInvokedFrom } = useRelatedIssuesActions();

	if (invokedFromSource) {
		setInvokedFrom(invokedFromSource);
	}

	const singleInstrumentationId = useRef(uuid());
	const aiResultViewedSummaryLengthAnalyticsRef = useRef<string | undefined>();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const relatedToIssueType = useRelatesToIssueLinkType();

	const [
		{ currentStep, isLinkedIssuePanelButtonTriggered, invokedFrom, resourceSuggestions },
		{
			setDisplayLinkedIssuesPanelCallback,
			discardAllSuggestions,
			setRelatedIssuesFetchStep,
			resetState,
			setRelatedIssuesDisplayStep,
			setRelatedIssuesLoadedStep,
			setResourceSuggestion,
		},
	] = useRelatedIssues();

	const { data, error, loading, fetchSuggestion } = useFetchRelatedIssue(existingIssues);

	useEffect(() => {
		if (data && fg('platformisation-of-proactive-collapsible-container')) {
			/**
			 * We want to move to local state instead of sweet state
			 * But, until Similar Issues Draft List is not platformised, we need to use sweet state
			 */
			data && setResourceSuggestion(data);
		}
	}, [data, setResourceSuggestion]);

	const draftSuggestions = useMemo(() => {
		const filteredSuggestions = resourceSuggestions?.filter(
			(suggestion) => suggestion.resourceStatus === 'DRAFT',
		);
		return filteredSuggestions;
	}, [resourceSuggestions]);

	const getAttributes = useCallback(
		() => ({
			...getAiEventAttributes(invokedFrom),
			singleInstrumentationId: singleInstrumentationId.current,
		}),
		[invokedFrom],
	);

	const { acceptAllSuggestions } = useRelatedIssuesActions();

	const newAttributes: RelatedIssuesAttributes = getAttributes();

	const oldAttributes: RelatedIssuesAttributes = {
		...AI_EVENT_ATTRIBUTES,
		singleInstrumentationId: singleInstrumentationId.current,
	};

	const attributes = expVal('proactive-jira-ai-similar-issues', 'isEnabled', false)
		? newAttributes
		: oldAttributes;

	// Analytics callbacks
	const onAiInteractionInitiatedEvent = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction initiated', {
			invokedFrom,
			existingItemsCount: existingIssues.length,
			...attributes,
		});
	}, [createAnalyticsEvent, invokedFrom, existingIssues.length, attributes]);

	// Base function for aiResult viewed events
	const onAiResultViewedEvent = useCallback(
		(doesNotMeetMAUCriteria: boolean, suggestionsExpanded: boolean, currentData?: typeof data) => {
			if (!currentData) return;

			if (
				invokedFrom === 'globalIssueCreate' &&
				aiResultViewedSummaryLengthAnalyticsRef &&
				aiResultViewedSummaryLengthAnalyticsRef.current &&
				expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)
			) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					doesNotMeetMAUCriteria,
					summaryLength: aiResultViewedSummaryLengthAnalyticsRef?.current?.length,
					invokedFrom,
					aiSuggestedCount: currentData.length,
					suggestionsExpanded,
					...attributes,
				});
			} else {
				fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult viewed', {
					doesNotMeetMAUCriteria,
					aiSuggestedCount: currentData.length,
					suggestionsExpanded,
					...attributes,
				});
			}
		},
		[createAnalyticsEvent, attributes, invokedFrom],
	);

	const dataRef = useRef(data);
	useEffect(() => {
		dataRef.current = data;
	}, [data]);

	const onAiResultViewedEventWhenCollapsed = useCallback(() => {
		onAiResultViewedEvent(true, false, dataRef.current);
	}, [onAiResultViewedEvent]);

	const onAiResultViewedEventWhenExpanded = useCallback(() => {
		onAiResultViewedEvent(false, true, dataRef.current);
	}, [onAiResultViewedEvent]);

	const onAiResultActionedEvent = useCallback(() => {
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiResult actioned',
			'aiRelatedIssuesSuggestionResultBox',
			{
				aiResultAction: 'suggestionsHidden',
				aiItemsCount: data?.length,
				...attributes,
			},
		);
	}, [createAnalyticsEvent, data?.length, attributes]);

	const onAiResultErrorEvent = useCallback(
		(errorReceived: unknown) => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult error',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiErrorCode:
						errorReceived instanceof FetchError ? errorReceived.statusCode : UNCATEGORISED_ERROR,
					...(fg('ai-error-message-similar-issues')
						? {
								aiErrorMessage:
									errorReceived instanceof FetchError
										? errorReceived.message
										: JSON.stringify(errorReceived),
							}
						: {}),
					...attributes,
				},
			);
		},
		[createAnalyticsEvent, attributes],
	);

	const onAiFeedbackSubmittedEvent = useCallback(
		(aiFeedbackResult: 'up' | 'down') => {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiFeedback submitted',
				'aiRelatedIssuesSuggestionResultBox',
				{ aiFeedbackResult },
			);
		},
		[createAnalyticsEvent],
	);

	const onDisplaySuggestions = useCallback(() => {
		setRelatedIssuesDisplayStep();
	}, [setRelatedIssuesDisplayStep]);

	/**
	 * Refactor to use local state instead of sweet state in GRAVITYAI-2715 by passing back the data from ProactiveContainer
	 * and using that to set local state in the parent container here
	 */
	const onLoadedSuggestions = useCallback(() => {
		setRelatedIssuesLoadedStep();
	}, [setRelatedIssuesLoadedStep]);

	const onFetchSuggestion = useCallback(
		(customRelatedIssueSearchPayload?: RelatedIssuesSearchPayload) => {
			fetchSuggestion(customRelatedIssueSearchPayload);
			setRelatedIssuesFetchStep();
			if (expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false)) {
				aiResultViewedSummaryLengthAnalyticsRef.current =
					customRelatedIssueSearchPayload?.issue_summary;
			}
		},
		[fetchSuggestion, setRelatedIssuesFetchStep],
	);

	const [shouldReinitiate, setShouldReinitiate] = useState<number>(0);
	const setTriggerReinitiate = useCallback(() => {
		setShouldReinitiate(shouldReinitiate + 1);
	}, [shouldReinitiate]);

	/**
	 * When we are in the non-proactive mode, we use this component to control the display of the modal.
	 */
	useEffect(() => {
		if (!expVal('proactive-jira-ai-similar-issues', 'isEnabled', false)) {
			// create an artificial blocker to prevent
			// panelDisplay effect on first render
			if (panelDisplay && shouldDisplayModal) {
				setRelatedIssuesFetchStep();
			}
			if (!shouldDisplayModal) {
				setShouldDisplayModal(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [panelDisplay, setRelatedIssuesFetchStep]);

	useEffect(() => {
		if (!expVal('proactive-jira-ai-similar-issues', 'isEnabled', false)) {
			// @ts-expect-error onRelatedIssuesTriggered will always be defined in non-proactive mode
			setDisplayLinkedIssuesPanelCallback(onRelatedIssuesTriggered);
		}
	}, [onRelatedIssuesTriggered, setDisplayLinkedIssuesPanelCallback]);

	/**
	 * In proactive mode, state is reset inside the collapsible parent.
	 */
	useEffect(() => {
		// reset on unmount
		return () => {
			if (!expVal('proactive-jira-ai-similar-issues', 'isEnabled', false)) {
				resetState();
			}
		};
	}, [issueKey, resetState]);

	const onAcceptAllSuggestions = useCallback(() => {
		if (!draftSuggestions) {
			return;
		}
		for (const suggestion of draftSuggestions) {
			const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${suggestion.resource.id}`;
			onIssueLinkSave(
				optimisticId,
				suggestion.resource,
				relatedToIssueType,
				createAnalyticsEvent({}),
			);
		}
		acceptAllSuggestions();
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'aiResult actioned',
			'aiRelatedIssuesSuggestionResultBox',
			{
				aiResultAction: 'bulkIssuesAccepted',
				aiItemsCount: draftSuggestions.length,
			},
		);
	}, [
		acceptAllSuggestions,
		createAnalyticsEvent,
		draftSuggestions,
		onIssueLinkSave,
		relatedToIssueType,
	]);

	const footerActionNode = useMemo(
		() => (
			<Footer
				draftSuggestions={draftSuggestions}
				onAcceptAllSuggestions={onAcceptAllSuggestions}
				invokedFrom={invokedFrom}
			/>
		),
		[draftSuggestions, onAcceptAllSuggestions, invokedFrom],
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			{expVal('proactive-jira-ai-similar-issues', 'isEnabled', false) ? (
				<>
					{fg('platformisation-of-proactive-collapsible-container') ? (
						<ProactiveContainer
							initialStep="FETCHING"
							data={draftSuggestions}
							error={error}
							loading={loading}
							onFetchSuggestions={onFetchSuggestion}
							resultNumber={draftSuggestions?.length}
							onDismissSuggestions={discardAllSuggestions}
							onDisplaySuggestions={onDisplaySuggestions}
							onLoadedSuggestions={onLoadedSuggestions}
							invokedFrom={invokedFrom}
							onAiInteractionInitiatedEvent={onAiInteractionInitiatedEvent}
							onAiResultErrorEvent={onAiResultErrorEvent}
							onAiResultViewedEventWhenCollapsed={onAiResultViewedEventWhenCollapsed}
							onAiResultViewedEventWhenExpanded={onAiResultViewedEventWhenExpanded}
							onAiResultActionedEvent={onAiResultActionedEvent}
							onAiFeedbackSubmittedEvent={onAiFeedbackSubmittedEvent}
							messages={{
								headerTitle,
								errorMessage: messages.similarIssuesCouldNotBeGeneratedIssueTermRefresh,
								noItemsMessage: messages.searchAgain,
								viewItemsMessage: messages.showNumResults,
								findingResultsMessage: messages.searchingForSimilarWorkItems,
							}}
							customRelatedIssueSearchPayload={props.customRelatedIssueSearchPayload}
							isBeta
							shouldReinitiate={shouldReinitiate}
							setTriggerReinitiate={setTriggerReinitiate}
							feedbackCollectorId={FEEDBACK_COLLECTOR_ID}
							feedbackFormEntrypointId={FEEDBACK_ENTRYPOINT_ID}
							aiExperience={RELATED_ISSUES_AI_EXPERIENCE}
							footerActionNode={footerActionNode}
						>
							<RelatedIssuesDraftList
								onCopyButtonClick={onCopyButtonClick}
								invokedFrom={invokedFrom}
								onIssueLinkSave={onIssueLinkSave}
							/>
						</ProactiveContainer>
					) : (
						<ProactiveSimilarIssuesCollapsible
							existingIssues={existingIssues}
							customRelatedIssueSearchPayload={props.customRelatedIssueSearchPayload}
						>
							<RelatedIssuesDraftList
								onCopyButtonClick={onCopyButtonClick}
								invokedFrom={invokedFrom}
								onIssueLinkSave={onIssueLinkSave}
							/>
							<AiFooter isBeta={false} />
						</ProactiveSimilarIssuesCollapsible>
					)}
				</>
			) : (
				<SuggestRelatedIssuesModal
					currentStep={currentStep}
					isLinkedIssuePanelButtonTriggered={isLinkedIssuePanelButtonTriggered}
					onDiscardAllSuggestions={discardAllSuggestions}
					existingIssues={existingIssues}
					onIssueLinkSave={onIssueLinkSave}
				/>
			)}
		</ContextualAnalyticsData>
	);
};

const AiRelatedIssuesResourcesEntrypoint = ({ props }: EntryPointProps<{}, {}, Props, {}>) => {
	return (
		<JiraIssueRelatedIssuesContainer scope={`related-issues-${props.issueKey}`}>
			<SuggestRelatedIssuesModalContainer {...props} />
		</JiraIssueRelatedIssuesContainer>
	);
};

export default AiRelatedIssuesResourcesEntrypoint;
